import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCatalogoFarmacosControllerClassesTerapeuticasQueryKey,
  getCatalogoFarmacosControllerListarQueryKey,
  getCatalogoFarmacosControllerSugestoesQueryKey,
  getCatalogoFarmacosControllerObterQueryKey,
  useCatalogoFarmacosControllerAtualizar,
  useCatalogoFarmacosControllerClassesTerapeuticas,
  useCatalogoFarmacosControllerCriar,
} from '@/api/generated/admin-catalogo-farmacos/admin-catalogo-farmacos';
import type { CatalogoFarmacoResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { ChipsDeSinonimos } from '@/shared/components/ChipsDeSinonimos';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ComboBox } from '@/shared/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

const SEM_CLASSE = '';

const schema = z.object({
  nome: z.string().trim().min(2, 'Informe o princípio ativo').max(200),
  sinonimos: z.array(z.string().trim().min(1).max(120)).max(20, 'No máximo 20 sinônimos'),
  classeTerapeutica: z.string().trim().max(120),
  unidadeDosePadrao: z.string().trim().max(20),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  farmaco?: CatalogoFarmacoResponse;
}

/**
 * Criar/editar o PRINCÍPIO ATIVO.
 *
 * ⚠️ O nome é "Trilostano", não "Vetoryl". A marca entra como apresentação
 * comercial, na tela do fármaco — é o princípio ativo que tem dose comparável
 * entre pacientes, e é por ele que a Análise Comparativa agrupa a coorte.
 */
export function FarmacoFormDialogo({ aberto, aoFechar, farmaco }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(farmaco);

  const criar = useCatalogoFarmacosControllerCriar();
  const atualizar = useCatalogoFarmacosControllerAtualizar();

  // As classes já em uso, para o admin escolher em vez de redigitar. O combo
  // aceita texto livre: classe nova é caso normal, e obrigar a lista fechada
  // travaria o cadastro do primeiro fármaco de uma classe.
  const { data: classes } = useCatalogoFarmacosControllerClassesTerapeuticas({
    query: { enabled: aberto },
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;
    reset({
      nome: farmaco?.nome ?? '',
      sinonimos: farmaco ? [...farmaco.sinonimos] : [],
      classeTerapeutica: farmaco?.classeTerapeutica ?? SEM_CLASSE,
      unidadeDosePadrao: farmaco?.unidadeDosePadrao ?? '',
    });
  }, [aberto, farmaco, reset]);

  const sinonimos = watch('sinonimos') ?? [];
  const nome = watch('nome') ?? '';
  const classeTerapeutica = watch('classeTerapeutica') ?? SEM_CLASSE;

  const opcoesDeClasse = [
    { valor: SEM_CLASSE, rotulo: 'Sem classe terapêutica' },
    ...(classes?.itens ?? []).map((c) => ({ valor: c, rotulo: c })),
  ];

  const aoEnviar = handleSubmit(async (dados) => {
    const opcional = (v: string) => (v.trim() ? v.trim() : null);

    const corpo = {
      nome: dados.nome,
      sinonimos: dados.sinonimos,
      classeTerapeutica: opcional(dados.classeTerapeutica),
      unidadeDosePadrao: opcional(dados.unidadeDosePadrao),
    };

    try {
      if (farmaco) {
        await atualizar.mutateAsync({ id: farmaco.id, data: corpo });
        await queryClient.invalidateQueries({
          queryKey: getCatalogoFarmacosControllerObterQueryKey(farmaco.id),
        });
      } else {
        await criar.mutateAsync({ data: corpo });
      }

      await queryClient.invalidateQueries({
        queryKey: getCatalogoFarmacosControllerListarQueryKey(),
      });
      // A fila de sugestões olha a MESMA tabela por outro recorte (`origem`):
      // completar uma sugestão muda o que ela mostra, e sem esta linha a aba
      // continuaria exibindo a linha com os campos antigos.
      await queryClient.invalidateQueries({
        queryKey: getCatalogoFarmacosControllerSugestoesQueryKey(),
      });
      // A classe recém-digitada precisa aparecer no filtro da listagem sem F5.
      await queryClient.invalidateQueries({
        queryKey: getCatalogoFarmacosControllerClassesTerapeuticasQueryKey(),
      });

      toast.success(editando ? 'Fármaco atualizado.' : 'Fármaco criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o fármaco.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar fármaco' : 'Novo fármaco'}</DialogTitle>
          <DialogDescription>
            O nome é o princípio ativo (Trilostano), não a marca. As marcas entram como
            apresentações comerciais, na tela do fármaco.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="nome" rotulo="Princípio ativo" erro={errors.nome?.message} obrigatorio>
            <Input
              id="nome"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: Trilostano"
              {...register('nome')}
            />
          </Campo>

          <Campo
            id="sinonimos"
            rotulo="Sinônimos"
            erro={errors.sinonimos?.message}
            dica="Outras grafias e siglas do princípio ativo. A busca casa por elas."
          >
            <ChipsDeSinonimos
              id="sinonimos"
              sinonimos={sinonimos}
              nomeDoItem={nome}
              aoMudar={(novos) => setValue('sinonimos', novos, { shouldDirty: true })}
            />
          </Campo>

          <Campo
            id="classeTerapeutica"
            rotulo="Classe terapêutica"
            erro={errors.classeTerapeutica?.message}
            dica="Só agrupa a listagem. Escolha uma existente para não criar duas grafias da mesma classe."
          >
            <ComboBox
              id="classeTerapeutica"
              valor={classeTerapeutica}
              rotuloSelecionado={classeTerapeutica}
              aoEscolher={(v) => setValue('classeTerapeutica', v, { shouldDirty: true })}
              opcoes={opcoesDeClasse}
              placeholder="Sem classe terapêutica"
              placeholderBusca="Buscar ou digitar uma classe..."
              permitirLivre
              rotuloLivre={(termo) => `Criar a classe ${termo}`}
            />
          </Campo>

          <Campo
            id="unidadeDosePadrao"
            rotulo="Unidade de dose padrão"
            erro={errors.unidadeDosePadrao?.message}
            dica="Como a dose deste princípio ativo é escrita. Ex.: mg/kg, UI/kg."
          >
            <Input
              id="unidadeDosePadrao"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: mg/kg"
              {...register('unidadeDosePadrao')}
            />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar fármaco'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
